import {
  Avatar,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Icon, MenuItem, PermissionButton } from "@pufflig/prompt-studio-ui";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

interface TeamRowData {
  id: string;
  member?: {
    name: string;
    picture: string;
  };
  email: string;
  accessLevel: "team_owner" | "team_editor" | "team_viewer";
  isPending?: boolean;
}

interface TableProps {
  data: TeamRowData[];
  isReadOnly?: boolean;
  onSelectRow?: (rowId: string) => void;
  onRemoveTeamMember: (rowId: string) => void;
  onCancelInvite?: (inviteId: string) => void;
  onResendInvite?: (inviteId: string) => void;
  onPermissionChange?: (rowId: string, permission: "viewer" | "editor") => void;
}

const columnHelper = createColumnHelper<TeamRowData>();

const permissionMap = {
  team_viewer: "viewer",
  team_editor: "editor",
  team_owner: "owner",
} as const;

export const TeamMemberList = (props: TableProps) => {
  const { data, isReadOnly, onSelectRow, onCancelInvite, onResendInvite, onRemoveTeamMember, onPermissionChange } =
    props;

  const isLargeScreen = useBreakpointValue({
    base: false,
    lg: true,
  });

  const columns = [
    columnHelper.accessor("member", {
      header: "Name",
      cell: (info) => (
        <Stack direction="row" alignItems="center">
          {info.getValue() && <Avatar size="sm" name={info.getValue()?.name} src={info.getValue()?.picture} />}
          <Text overflow="hidden" textOverflow="ellipsis" display={isLargeScreen ? "inline-block" : "none"}>
            {info.getValue()?.name}
          </Text>
        </Stack>
      ),
    }),
    columnHelper.accessor("email", {
      header: "Email",
      cell: (info) => (
        <Flex>
          <Text textStyle="label/M/regular" overflow="hidden" textOverflow="ellipsis">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("accessLevel", {
      header: "Access level",
      cell: (info) => {
        return (
          <Flex>
            <PermissionButton
              isDisabled={isReadOnly}
              permission={info.row.original.isPending ? "pending" : permissionMap[info.getValue()]}
              onPermissionChange={(permission) => onPermissionChange?.(info.row.original.id, permission)}
            />
          </Flex>
        );
      },
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: "",
      size: 36,
      cell: (info) => {
        const isPending = info.row.original.isPending;
        const isOwner = info.row.original.accessLevel === "team_owner";

        if (isOwner) return null;

        return (
          <Flex justifyContent="flex-end">
            <Menu>
              <MenuButton
                isDisabled={isReadOnly}
                as={IconButton}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                variant="ghost"
                size="sm"
                aria-label="options"
                icon={<Icon name="more" />}
                _hover={{ bg: "background.tertiary" }}
              >
                Actions
              </MenuButton>
              <MenuList>
                {isPending && (
                  <MenuItem
                    primaryLabel="Resend"
                    onClick={(e) => {
                      e.stopPropagation();
                      onResendInvite?.(info.getValue() as string);
                    }}
                  />
                )}
                <MenuItem
                  primaryLabel="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isPending) {
                      onCancelInvite?.(info.getValue() as string);
                    } else {
                      onRemoveTeamMember(info.getValue() as string);
                    }
                  }}
                />
              </MenuList>
            </Menu>
          </Flex>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableMultiRowSelection: true,
    enableRowSelection: true,
    autoResetAll: true,
  });

  return (
    <Flex direction="column" height="100%" flex="1">
      <TableContainer flex="1" overflowX="auto">
        <Table>
          <Thead height="3.375rem">
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    padding="0"
                    px="1.5rem"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    width={
                      header.id === "member" && !isLargeScreen
                        ? "1em"
                        : header.id === "actions"
                          ? "2em"
                          : header.id === "accessLevel"
                            ? "4em"
                            : "8em"
                    }
                  >
                    <Text textStyle="label/S/regular" color="foreground.secondary" textTransform="none">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                _hover={{
                  bg: "background.secondary",
                }}
                height="3.375rem"
                cursor={"pointer"}
                onClick={() => onSelectRow?.(row.original.id)}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    key={cell.id}
                    width={
                      cell.column.id === "member" && !isLargeScreen
                        ? "1em"
                        : cell.column.id === "actions"
                          ? "2em"
                          : cell.column.id === "accessLevel"
                            ? "4em"
                            : "8em"
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};
